// client/src/components/WishlistContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_URL } from '../config';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);

  // Check if user is logged in
  const isAuthenticated = () => {
    return !!localStorage.getItem('token');
  };

  // Fetch wishlist from backend (for authenticated users)
  const fetchWishlist = async () => {
    if (!isAuthenticated()) {
      // Load from localStorage for guest users
      const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
      setWishlist(guestWishlist);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/wishlist`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWishlist(data.wishlist || []);
      } else if (response.status === 401 || response.status === 403) {
        // Token is invalid or expired - clear it
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setWishlist([]);
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add product to wishlist
  const addToWishlist = async (product) => {
    if (!isAuthenticated()) {
      // Guest user - store in localStorage
      const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
      const exists = guestWishlist.some(item => item.product._id === product._id);

      if (exists) {
        return { success: false, error: 'Product already in wishlist' };
      }

      guestWishlist.push({ product, addedAt: new Date() });
      localStorage.setItem('guestWishlist', JSON.stringify(guestWishlist));
      setWishlist(guestWishlist);
      return { success: true };
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/wishlist/add/${product._id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        await fetchWishlist(); // Refresh wishlist
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return { success: false, error: 'Failed to add to wishlist' };
    }
  };

  // Remove product from wishlist
  const removeFromWishlist = async (productId) => {
    if (!isAuthenticated()) {
      // Guest user - remove from localStorage
      const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
      const updated = guestWishlist.filter(item => item.product._id !== productId);
      localStorage.setItem('guestWishlist', JSON.stringify(updated));
      setWishlist(updated);
      return { success: true };
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/wishlist/remove/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        await fetchWishlist(); // Refresh wishlist
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return { success: false, error: 'Failed to remove from wishlist' };
    }
  };

  // Toggle product in/out of wishlist
  const toggleWishlist = async (product) => {
    const isInWishlist = wishlist.some(item => {
      const itemProductId = item.product?._id || item.product;
      return itemProductId === product._id;
    });

    if (isInWishlist) {
      return await removeFromWishlist(product._id);
    } else {
      return await addToWishlist(product);
    }
  };

  // Check if product is in wishlist
  const isInWishlist = (productId) => {
    return wishlist.some(item => {
      const itemProductId = item.product?._id || item.product;
      return itemProductId === productId;
    });
  };

  // Clear entire wishlist
  const clearWishlist = async () => {
    if (!isAuthenticated()) {
      localStorage.removeItem('guestWishlist');
      setWishlist([]);
      return { success: true };
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/wishlist/clear`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setWishlist([]);
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Error clearing wishlist:', error);
      return { success: false, error: 'Failed to clear wishlist' };
    }
  };

  // Sync guest wishlist with database after login
  const syncGuestWishlist = async () => {
    const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');

    if (guestWishlist.length === 0) return;

    // Add all guest wishlist items to database
    for (const item of guestWishlist) {
      await addToWishlist(item.product);
    }

    // Clear guest wishlist
    localStorage.removeItem('guestWishlist');
    await fetchWishlist();
  };

  // Load wishlist on mount and when auth status changes
  useEffect(() => {
    fetchWishlist();
  }, []);

  const value = {
    wishlist,
    loading,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isInWishlist,
    clearWishlist,
    fetchWishlist,
    syncGuestWishlist,
    wishlistCount: wishlist.length
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContext;
